import { ButtCutMethodHeelSettings, HeelHeightMethodHeelSettings, HeelSettings } from '@paragon/contract-library';
import { convertDegreesToRadians, convertRadiansToDegrees, Direction2D } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

/**
 * When editing the heel settings on multiple planes of differing slope, we need a fallback value to calculate the top
 * chord's contribution to heel height. Also, see note on `defaultChordWidthForHeelHeightCalculations`.
 */
export const defaultTopChordSlopeInDegreesForHeelHeightCalculations = convertRadiansToDegrees(Math.atan(6.0 / 12.0));

export function heelSettingsAreExactlyEqual(settings1: HeelSettings, settings2: HeelSettings): boolean {
  const keysToCompare: (keyof HeelHeightMethodHeelSettings | keyof ButtCutMethodHeelSettings)[] = [
    'chordTypeRunThrough',
    'interiorButtCut',
  ];
  if (settings1.heelMethod === 'ButtCut' && settings2.heelMethod === 'ButtCut') {
    keysToCompare.push('buttCut');
  } else if (settings1.heelMethod === 'HeelHeight' && settings2.heelMethod === 'HeelHeight') {
    keysToCompare.push('heelHeight');
  } else return false;

  for (const key of keysToCompare) {
    if (settings1[key as keyof HeelSettings] !== settings2[key as keyof HeelSettings]) return false;
  }
  return true;
}

export const DEFAULT_HEEL_SETTINGS: HeelSettings = Object.freeze({
  heelMethod: 'ButtCut',
  buttCut: 0.25,
  interiorButtCut: 0.25,
  chordTypeRunThrough: 'Top',
});

export function calculateTotalHeelHeight(
  heelSettings: HeelSettings,
  topChordLumberWidth: number,
  topChordSlopeInDegrees: number,
): number | null {
  switch (heelSettings.heelMethod) {
    case 'HeelHeight':
      return heelSettings.heelHeight;
    case 'ButtCut':
      return Direction2D.fromAngle(topChordSlopeInDegrees).isParallelTo(Direction2D.up)
        ? null
        : heelSettings.buttCut + topChordLumberWidth / Math.cos(convertDegreesToRadians(topChordSlopeInDegrees));
    default:
      assertExhaustiveSwitch(heelSettings);
  }
}

export function editHeelSettings(settings: HeelSettings, heelHeight: number, buttCut: number): HeelSettings {
  switch (settings.heelMethod) {
    case 'HeelHeight':
      return { ...settings, heelHeight };

    case 'ButtCut':
      return { ...settings, buttCut };

    default:
      assertExhaustiveSwitch(settings);
  }
}
